import { useMutation } from '@tanstack/react-query';
import { ClinicalNoteApi } from '../../../apis/ClinicalNoteApi/ClinicalNoteApi';
import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { AppMedError } from '../../../error/AppMedError';
import { ClinicalNoteType } from '../ClinicalNote';

function sanitizePart(input: string): string {
    return input.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, '-');
}

function fileLabelForType(noteType: ClinicalNoteType): string {
    switch (noteType) {
        case ClinicalNoteType.PhysicalExam:
            return 'PhysicalExaminations';
        case ClinicalNoteType.MedicalHistory:
            return 'MedicalHistory';
        case ClinicalNoteType.Counselling:
            return 'CounsellingNotes';
        default:
            return 'ClinicalNotes';
    }
}

function buildPdfFilename(
    noteType: ClinicalNoteType,
    params: { patientName?: string | null; patientDateOfBirth?: string | null },
): string {
    const nameParts = (params.patientName || 'Unknown').trim().split(/\s+/);
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join('-') : 'Unknown';
    const dob = params.patientDateOfBirth ? params.patientDateOfBirth.split('T')[0] : 'Unknown';
    const pdfDate = new Date().toISOString().split('T')[0];
    const raw = `${fileLabelForType(noteType)}_${sanitizePart(lastName)}_${sanitizePart(firstName)}_${dob}_${pdfDate}.pdf`;
    return raw.length > 200 ? raw.substring(0, 196) + '.pdf' : raw;
}

interface DownloadClinicalNotesPdfParams {
    patientId: string;
    language: string;
    includeActivityLog: boolean;
    patientName?: string | null;
    patientDateOfBirth?: string | null;
}

export function useDownloadClinicalNotesPdf(noteType: ClinicalNoteType, _i18nPrefix: string) {
    return useMutation<Blob, AppMedError, DownloadClinicalNotesPdfParams>({
        mutationKey: [QueryKeys.clinicalNotes, noteType, 'downloadPdf'],
        mutationFn: async ({ patientId, language, includeActivityLog }) => {
            return await ClinicalNoteApi.downloadClinicalNotesPdf(patientId, noteType, language, includeActivityLog);
        },
        onSuccess: (blob, params) => {
            const filename = buildPdfFilename(noteType, params);
            ClinicalNoteApi.downloadPdfFile(blob, filename);
        },
    });
}
